import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';

const PriceTicker = () => {
  const [priceData, setPriceData] = useState({
    price: 0.00001942,
    change24h: 0.00,
    isLive: false
  });

  // Fetch price via our backend proxy (/api/bonding-curve).
  // Previously this hit DexScreener directly with a stale hardcoded pair id —
  // permanently OFFLINE + third-party requests from every visitor's browser.
  const fetchPrice = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/bonding-curve`,
        { method: 'GET', headers: { 'Accept': 'application/json' } }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newPrice = parseFloat(data?.priceUsd || 0);

      if (data?.ok && newPrice > 0) {
        setPriceData({
          price: newPrice,
          change24h: parseFloat(data.priceChange24h || 0),
          isLive: true
        });
      } else {
        throw new Error('No pair data found');
      }
    } catch (error) {
      // Keep last known price if API fails, just update live status
      setPriceData(prev => ({
        ...prev,
        isLive: false
      }));
    }
  };

  useEffect(() => {
    fetchPrice();
    // Mobile: 60s interval — 10s forces GPU repaint every 10s via backdrop-blur (causes scroll freeze)
    // Desktop: 30s is fine
    const isMobile = window.innerWidth <= 768;
    const interval = setInterval(fetchPrice, isMobile ? 60000 : 30000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (price < 0.001) {
      return price.toFixed(8);
    }
    return price.toFixed(6);
  };

  const formatChange = (change) => {
    const formatted = Math.abs(change).toFixed(2);
    return change >= 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-black/80 md:bg-black/15 md:backdrop-blur-lg">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold bg-gradient-to-r from-solana-primary to-neon-cyan bg-clip-text text-transparent">
              SOLVIBE ($SVB)
            </span>
          </div>
          
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-gray-400">Price:</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={priceData.price}
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 10, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-white font-semibold"
                >
                  ${formatPrice(priceData.price)}
                </motion.span>
              </AnimatePresence>
            </div>
            
            <div className="flex items-center gap-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${priceData.change24h >= 0}-${priceData.change24h}`}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex items-center gap-1 ${
                    priceData.change24h >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {priceData.change24h >= 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  <span className="font-semibold">
                    {formatChange(priceData.change24h)} 24h
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <div className={`w-1.5 h-1.5 rounded-full ${priceData.isLive ? 'bg-green-400' : 'bg-gray-400'} animate-pulse`}></div>
            <span className="text-xs text-gray-400 uppercase font-semibold">
              {priceData.isLive ? 'LIVE' : 'OFFLINE'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceTicker;